import SliderData from './SliderData';
import type { ValueHumanDescription } from './types';
import { ColorPicker } from '../ColorPicker2/ColorPicker';
import type { ColorPickerInstance } from '../ColorPicker2/ColorPicker';
import ColorizerStorage from './ColorizerStorage';
import { smartRounding } from '../../tools/smartRounding';
import { svgDrawer } from './svgDrawer';
import { featureGetBestColor } from './featureGetBestColor';
import './ColorizeSlider.scss';
import { ResolutionConverter, type ResolutionType } from './ResolutionConverter';
import { ResolutionDate } from './ResolutionDate';

type SliderModeType = 'colorize' | 'range';

interface ColorizeSliderOptions {
    enableAddRemovePeriods?: boolean;
    enableColorPicker?: boolean;
    darkMode?: boolean;
    sliderMode: SliderModeType;
    smallInputs?: boolean;
    inputsAboveSlider?: boolean;
    dangerZoneBelowValue?: number | false;
    dangerZoneAboveValue?: number | false;
    enableSmartRounding?: boolean;
    periodMode?: boolean;
    indicatorColor?: string;
    hiddenInputsOutsideMinMax?: boolean;
    resolution: ResolutionType;
    resolutionValue?: number;
    flagOnZero?: string;
}

interface ColorizeSliderCallbacks {
    onSliderChange: (sliderData: SliderData) => void;
    onSliderDragMove: (sliderData: SliderData) => void;
    onChangePeriodMode?: (periodMode: boolean) => void;
}

class ColorizeSlider {
    private readonly element: HTMLDivElement;
    private sliderData: SliderData;
    private readonly sliderGripWidthPx: number;
    private enableShowingAddRemoveIcons: boolean;
    private readonly enableAddRemovePeriods: boolean;
    private readonly enableColorPicker: boolean;
    private readonly darkMode: boolean;
    private colorPicker: ColorPickerInstance;
    private callbacks: ColorizeSliderCallbacks;
    private readonly sliderHeightPx: number = 50;
    private options: ColorizeSliderOptions;
    private readonly enablePeriodModeByClickOnSlider: boolean = false;
    private readonly minimumWidthForTrianglesPx: number = 20;
    private readonly strokeWidthForValueIndicator: number = 3;
    private readonly resolutionConverter: ResolutionConverter = new ResolutionConverter(
        'number',
        1,
        true
    );

    constructor(
        sliderData: SliderData,
        options: ColorizeSliderOptions,
        callbacks: ColorizeSliderCallbacks
    ) {
        this.element = document.createElement('div');
        this.sliderData = sliderData;
        this.sliderGripWidthPx = 13;
        this.enableAddRemovePeriods = options.enableAddRemovePeriods ?? true;
        this.enableColorPicker = options.enableColorPicker ?? true;
        this.darkMode = options.darkMode ?? true;
        this.enableShowingAddRemoveIcons = true;
        this.sliderHeightPx = sliderData.getSliderHeight();
        this.options = {
            ...options,
            resolution: options.resolution ?? 'number',
            resolutionValue: options.resolutionValue ?? 1,
            flagOnZero: options.flagOnZero ?? '',
        };

        this.resolutionConverter.setResolution(
            this.options.resolution,
            this.options.resolutionValue
        );

        if (this.resolutionConverter.canExtendRange()) {
            // console.log('ColorizeSlider, canExtendRange', this.sliderData.getVisibleMin(), this.sliderData.getVisibleMax());
            // console.log('ColorizeSlider, canExtendRange, blocks1', this.sliderData.getBlocks().map(b=> b.max));
            this.sliderData.setVisibleMinMax(
                Math.min(this.sliderData.getVisibleMin(), this.sliderData.getBlock(0).max),
                Math.max(
                    this.sliderData.getVisibleMax(),
                    this.sliderData.getBlock(this.sliderData.getBlocks().length - 1).max
                ),
                false,
                false
            );
            // console.log('ColorizeSlider, canExtendRange, blocks2', this.sliderData.getBlocks().map(b=> b.max));
        }

        if (this.options.dangerZoneBelowValue && this.options.dangerZoneAboveValue) {
            if (this.options.dangerZoneBelowValue > this.options.dangerZoneAboveValue) {
                throw new Error('dangerZoneBelowValue must be less than dangerZoneAboveValue');
            }
        }

        this.options.indicatorColor = this.options.indicatorColor ?? '#000000';
        this.options.hiddenInputsOutsideMinMax = options.hiddenInputsOutsideMinMax ?? false;

        if (this.sliderData.getBlocks().length === 2) {
            this.enablePeriodModeByClickOnSlider = true;
        }

        this.callbacks = callbacks;
        this.colorPicker = ColorPicker({
            onColorChange: (block_id: number, color: string) => {
                console.log(
                    'ColorizeSlider, onColorChange, block_id',
                    this.sliderData.getBlock(block_id)
                );
                this.sliderData.setBlockColor(block_id, color);
                this.reloadSVGBackground();
            },
            onColorPickerClose: () => {
                console.log('ColorizeSlider, onColorPickerClose');
            },
        });
    }

    public setDangerZones(below: number | false, above: number | false): void {
        this.options.dangerZoneBelowValue = below;
        this.options.dangerZoneAboveValue = above;
    }

    public setPeriodMode(periodMode: boolean): void {
        this.options.periodMode = periodMode;
        if (periodMode && this.options.sliderMode === 'range') {
            this.sliderData.setInfinityOnBlock(0, '');
            this.sliderData.setInfinityOnBlock(1, '');
        }
        this.callbacks.onChangePeriodMode?.(periodMode);
        this.reload();
    }

    public getPeriodMode(): boolean {
        return this.options.periodMode ?? false;
    }

    public setSliderData(sliderData: SliderData): void {
        console.log('ColorizeSlider, setSliderData', sliderData);
        this.sliderData = sliderData;
    }

    public getSliderData(): SliderData {
        return this.sliderData;
    }

    public setIsSmartRoundingEnabled(isSmartRoundingEnabled: boolean): void {
        // this.sliderData.setIsSmartRoundingEnabled(isSmartRoundingEnabled);
        ColorizerStorage.setIsSmartRoundingEnabled(isSmartRoundingEnabled);
    }

    public getSlider(): HTMLDivElement {
        this.reload();
        return this.element;
    }

    public reload(): boolean {
        // console.log('getQuantile, ColorizeSlider, reload, sliderData', this.sliderData);

        this.element.innerHTML = this.getHTML();
        this.element.setAttribute(
            'class',
            `telematics-slider ${this.darkMode ? 'dark-mode' : 'light-mode'}`
        );

        // toggle period mode when user clicks on the slider
        let draggable_container = this.element.querySelector(
            '.js-draggable-container'
        ) as HTMLDivElement;
        if (
            draggable_container &&
            this.options.sliderMode === 'range' &&
            this.enablePeriodModeByClickOnSlider === true
        ) {
            draggable_container.addEventListener('click', (e: MouseEvent) => {
                this.setPeriodMode(!this.getPeriodMode());
                if (typeof this.callbacks.onChangePeriodMode === 'function') {
                    this.callbacks.onChangePeriodMode(this.getPeriodMode());
                }
            });
        }

        if (this.sliderData.getVisibleMax() - this.sliderData.getVisibleMin() <= 0) {
            console.error(
                'ColorizeSlider, reload, sliderData.getVisibleMax() - sliderData.getVisibleMin() <= 0'
            );
            this.element.innerHTML = `
                <div class="slider-error">
                    Brak poprawnych danych
                </div>`;
            return false;
        }

        if (this.options.flagOnZero !== '') {
            let flagOnZero = document.createElement('div');
            flagOnZero.className = 'tg-ui-color-multi-range-flag-on-zero';
            flagOnZero.innerHTML = `
                <div style="width: 1px; height: 19px; border-left: 1px solid #1e5ec9; position:absolute; bottom:17px; left:200px;"></div>
                <div style="width: auto; height: 24px; position:absolute; bottom:36px; left:200px; border-radius:3px; background-color:white; color:#1e5ec9; font-weight:600; padding:3px 5px; transform: translateX(-50%); align-items: center; display: flex;">${this.options.flagOnZero}</div>
            `;

            this.element.appendChild(flagOnZero);
        }

        let color_picker_parent = document.querySelector('.geocoding-app') as HTMLDivElement;
        if (color_picker_parent) {
            let color_picker_element = color_picker_parent.querySelector(
                '.js-color-picker'
            ) as HTMLDivElement;
            if (!color_picker_element) {
                color_picker_element = document.createElement('div');
                color_picker_element.className = 'js-color-picker';
                color_picker_element.style.zIndex = '30000';
                color_picker_element.style.top = '0px';
                color_picker_element.style.left = '0px';
                color_picker_element.style.position = 'absolute';
                color_picker_parent.appendChild(color_picker_element);
            }
            if (color_picker_element) {
                color_picker_element.innerHTML = '';
                color_picker_element.appendChild(this.colorPicker.div);
            }
        } else {
            console.warn('ColorizeSlider, reload, color_picker_parent not found');
            // throw new Error('ColorizeSlider, reload, color_picker_parent not found');
        }

        this.reloadDraggableElements();
        this.reloadSVGBackground();
        this.reloadInputsElements(true);

        // Display minimum value on the left side of slider
        let rounded_min: ValueHumanDescription = new smartRounding().getRoundedValues(
            this.sliderData.getVisibleMin(),
            this.sliderData.getUnit(),
            { convert_nicer: true }
        );

        // console.debug('ColorizeSlider, unit: ', this.sliderData.getUnit());
        let minInfo = document.createElement('div');
        minInfo.className = 'tg-ui-color-multi-range-value-min';
        minInfo.style.left = '0px';
        if (this.options.resolution !== 'number' && this.options.resolution !== 'today') {
            minInfo.innerHTML = `<span>${this.resolutionConverter.getFormatted(this.sliderData.getVisibleMin(), true)}</span>`;
        } else {
            if (this.options?.enableSmartRounding === true) {
                minInfo.innerHTML = `<span>${rounded_min.rounded_value.toString()}&nbsp;${rounded_min.rounded_title.toString()}&nbsp;${rounded_min.rounded_unit}</span>`;
            } else {
                minInfo.innerHTML = `<span>${this.sliderData.getVisibleMin().toString()}&nbsp;${this.sliderData.getUnit()}</span>`;
            }
        }
        this.element.appendChild(minInfo);

        // Display maximum value on the right side of slider
        let rounded_max: ValueHumanDescription = new smartRounding().getRoundedValues(
            this.sliderData.getVisibleMax(),
            this.sliderData.getUnit(),
            { convert_nicer: true }
        );
        let maxInfo = document.createElement('div');
        maxInfo.className = 'tg-ui-color-multi-range-value-max';
        maxInfo.style.left = `${this.sliderData.getSliderWidth()}px`;
        // console.debug('ColorizeSlider, rounded_max', rounded_max);
        if (this.options.resolution !== 'number' && this.options.resolution !== 'today') {
            maxInfo.innerHTML = `<span>${this.resolutionConverter.getFormatted(this.sliderData.getVisibleMax(), true)}</span>`;
        } else {
            if (this.options?.enableSmartRounding === true) {
                maxInfo.innerHTML = `<span>${rounded_max.rounded_value.toString()}&nbsp;${rounded_max.rounded_title.toString()}&nbsp;${rounded_max.rounded_unit}</span>`;
            } else {
                maxInfo.innerHTML = `<span>${this.sliderData.getVisibleMax().toString()}&nbsp;${this.sliderData.getUnit()}</span>`;
            }
        }
        this.element.appendChild(maxInfo);

        let add_icon = this.element.querySelector('.tg-ui-color-multi-range-add') as HTMLDivElement;
        if (add_icon) {
            add_icon.addEventListener('click', (e: MouseEvent) => {
                let block_id = parseInt((<HTMLElement>e.target).getAttribute('data-slider') ?? '');
                let new_max = this.sliderData.getMiddleValueOfThisBlock(block_id);
                console.log('add_icon clicked', block_id, new_max);
                let new_block_id = this.sliderData.addBlock(new_max, '#782782');
                // this.sliderData.setBlockColor(new_block_id, Config.analyticsColorsShort[new_block_id % Config.analyticsColorsShort.length]);
                this.sliderData.setBlockColor(new_block_id, '#FFFFFF');
                let newColor = new featureGetBestColor(new_block_id, this.sliderData).getColor();
                if (newColor) {
                    this.sliderData.setBlockColor(new_block_id, newColor);
                }
                this.reloadSVGBackground();
                this.reloadInputsElements();
                this.reloadDraggableElements();
                this.showAddRemoveIcons(false);
            });
        }

        let remove_icon = this.element.querySelector(
            '.tg-ui-color-multi-range-remove'
        ) as HTMLDivElement;
        if (remove_icon) {
            remove_icon.addEventListener('click', (e: MouseEvent) => {
                let block_id = parseInt((<HTMLElement>e.target).getAttribute('data-slider') ?? '');
                console.log('remove_icon clicked', block_id);
                if (
                    this.sliderData.ifBlockExists(block_id) &&
                    this.sliderData.getBlocks().length > 1
                ) {
                    this.sliderData.removeBlock(block_id);
                    this.reloadSVGBackground();
                    this.reloadInputsElements();
                    this.reloadDraggableElements();
                    this.showAddRemoveIcons(false);
                } else {
                    console.log(
                        'remove_icon clicked, but block does not exist or there is only one block'
                    );
                }
            });
        }

        return true;
    }

    private showAddRemoveIcons(visible: boolean): void {
        if (this.enableShowingAddRemoveIcons === true) {
            let add_icon = this.element.querySelector(
                '.tg-ui-color-multi-range-add'
            ) as HTMLDivElement;
            let remove_icon = this.element.querySelector(
                '.tg-ui-color-multi-range-remove'
            ) as HTMLDivElement;
            if (add_icon) {
                add_icon.style.display = visible ? 'block' : 'none';
            }
            if (remove_icon) {
                remove_icon.style.display = visible ? 'block' : 'none';
            }
        }
    }

    private addDraggableValuesIndicators(): void {
        const updateDraggableElement = (e: Event, ui: any, dragAction: 'drag' | 'stop') => {
            let this_draggable_element = e.target as HTMLDivElement;
            let block_id = parseInt(this_draggable_element.getAttribute('data-slider') ?? '');
            let new_left_px = parseInt(this_draggable_element.style.left.replace('px', ''));
            let min_range = 0;
            let max_range = this.sliderData.getSliderWidth();
            if (new_left_px <= min_range) {
                new_left_px = min_range;
                ui.position.left = new_left_px;
                this_draggable_element.style.left = `${new_left_px}px`;
                // this_draggable_element.style.left = `${new_left_px + this.sliderGripWidthPx / 2}px`;
                this.sliderData.setInfinityOnBlock(block_id, 'from');
                // console.log('Infinity from ON');
            } else {
                if (dragAction === 'drag') {
                    this.sliderData.setInfinityOnBlock(block_id, '');
                    // console.log('Infinity from OFF');
                }
            }
            if (new_left_px >= max_range) {
                new_left_px = max_range;
                ui.position.left = new_left_px;
                // this_draggable_element.style.left = `${new_left_px - this.sliderGripWidthPx / 2}px`;
                this_draggable_element.style.left = `${new_left_px}px`;
                // this_draggable_element.style.left = `${new_left_px}px`;
                this.sliderData.setInfinityOnBlock(block_id, 'to');
                // console.log('Infinity to ON');
            } else {
                if (dragAction === 'drag') {
                    this.sliderData.setInfinityOnBlock(block_id, '');
                    // console.log('Infinity to OFF');
                }
            }
            // console.log('updateDraggableElement', new_left_px);
            this.sliderData.updateBlockLeftPx(block_id, new_left_px, true);
            this.reloadSVGBackground();
            this.reloadInputsElements();
        };

        let draggable_container = this.element.querySelector(
            '.js-draggable-container'
        ) as HTMLDivElement;
        if (draggable_container) {
            // draggable_container.innerHTML = '';
            this.sliderData
                .getVisibleBlocksWithoutLast(this.options.sliderMode === 'colorize')
                .forEach(block => {
                    // <div class="telematics-tg-ui-color-multi-range-grip ui-draggable" style="height:30px;left:138.2844197557683px;" data-slider="3"></div>
                    let div = document.createElement('div');
                    let sliderGripWidth = this.getSliderGripWidth();
                    div.className = 'telematics-tg-ui-color-multi-range-grip ui-draggable';
                    if (this.sliderData.getBlocks().length <= 2) {
                        div.style.height = this.sliderHeightPx + 40 + 'px'; // make bigger for better dragging
                    } else {
                        div.style.height = this.sliderHeightPx + 'px';
                    }
                    div.style.width = sliderGripWidth + 'px';
                    if (block.left_px !== null) {
                        div.style.left = `${block.left_px - sliderGripWidth / 2 + 1}px`;
                    } else {
                        console.warn('block.left_px is null', block);
                    }
                    div.style.position = 'absolute';
                    div.style.opacity = '0.0';

                    div.setAttribute('data-slider', `${block.id}`);

                    // https://jqueryui.com/draggable/#events
                    // @ts-ignore -- not found jQuery
                    (<any>jQuery(div)).draggable({
                        axis: 'x',
                        start: () => {
                            console.log('Start');
                            this.showAddRemoveIcons(false);
                            this.enableShowingAddRemoveIcons = false;
                        },
                        drag: (e: Event, ui: any) => {
                            setTimeout(() => {
                                this.showAddRemoveIcons(false);
                                this.enableShowingAddRemoveIcons = false;
                                updateDraggableElement(e, ui, 'drag');
                                // this.sliderData.calculateLabelPositions();
                                this.reloadInputsElements(true);
                                this.callbacks.onSliderDragMove(this.sliderData);
                            }, 0);
                        },
                        stop: (e: Event, ui: any) => {
                            console.log('Stop');
                            this.enableShowingAddRemoveIcons = true;
                            // setTimeout is needed here not for smooth dragging, but to update slider after dragging
                            setTimeout(() => {
                                updateDraggableElement(e, ui, 'stop');
                                // this.sliderData.calculateLabelPositions();
                                this.reloadDraggableElements();
                                this.reloadSVGBackground();
                                this.reloadInputsElements(true);
                                this.callbacks.onSliderChange(this.sliderData);
                            }, 0);
                        },
                    });
                    if (draggable_container !== null) {
                        draggable_container.appendChild(div);
                    } else {
                        throw new Error('draggable_container is null');
                    }
                });
        }
    }

    private getSliderGripWidth(): number {
        let sliderGripWidth = this.sliderGripWidthPx;
        if (this.sliderData.getBlocks().length === 1) {
            sliderGripWidth = sliderGripWidth * 2;
        } else if (this.options.sliderMode === 'range') {
            if (
                Math.abs(
                    this.sliderData.getBlocks()[1].left_px - this.sliderData.getBlocks()[0].left_px
                ) <= this.minimumWidthForTrianglesPx
            ) {
                sliderGripWidth = this.strokeWidthForValueIndicator;
            }
        }
        return sliderGripWidth;
    }

    private addDraggablePeriodMargins(): void {
        const updateDraggableElement = (
            e: Event,
            ui: any,
            dragAction: 'drag' | 'stop',
            middle_px: number,
            middle_divider: number,
            current_unit: string,
            target_unit
        ) => {
            let this_draggable_element = e.target as HTMLDivElement;
            let new_left_px = parseInt(this_draggable_element.style.left.replace('px', ''));

            console.debug(
                'updateDraggableElement',
                new_left_px,
                middle_px,
                middle_divider,
                current_unit,
                target_unit
            );

            // let middle = this.sliderData.convertPxToValue(middle_px) * 100;
            let middle = new smartRounding().getOriginalValueFromRounded(
                this.sliderData.convertPxToValue(middle_px),
                1,
                '',
                ''
            );
            let new_padding = Math.abs(middle - this.sliderData.convertPxToValue(new_left_px));

            console.debug('updateDraggableElement', middle, new_padding);

            this.sliderData.setMiddleValuesForTwoBlocks(0, 1, middle, new_padding);

            // this.sliderData.setInfinityOnBlock(0, '' );
            // this.sliderData.setInfinityOnBlock(1, '' );

            this.reloadSVGBackground();
            this.reloadInputsElements();
        };

        let draggable_container = this.element.querySelector(
            '.js-draggable-container'
        ) as HTMLDivElement;
        if (draggable_container) {
            // const middle_px = this.sliderData.getBlocks()[0].left_px + Math.abs(this.sliderData.getBlocks()[1].left_px - this.sliderData.getBlocks()[0].left_px);
            const middle = this.sliderData.getPeriodValuesForTwoBlocks(0, 1);
            // const middle_px = this.sliderData.convertValueToPx(middle.middle.non_rounded_value);
            const middle_px = this.sliderData.convertValueToPx(middle.middle.original_value);

            console.debug('updateDraggableElement, middle', middle, middle_px);
            // const middle_px = this.sliderData.convertValueToPx(this.sliderData.getMiddleValueOfThisBlock(1));
            // console.debug('updateDraggableElement, init, middle_px', middle_px, this.sliderData.getBlocks());

            // draggable_container.innerHTML = '';
            this.sliderData.getVisibleBlocksWithoutLast().forEach(block => {
                // <div class="telematics-tg-ui-color-multi-range-grip ui-draggable" style="height:30px;left:138.2844197557683px;" data-slider="3"></div>
                let div = document.createElement('div');
                div.className = 'telematics-tg-ui-color-multi-range-grip ui-draggable';
                let sliderGripWidth = this.getSliderGripWidth();
                div.style.height = this.sliderHeightPx + 'px';
                div.style.width = sliderGripWidth + 'px';
                if (block.left_px !== null) {
                    div.style.left = `${block.left_px - sliderGripWidth / 2 + 1}px`;
                } else {
                    console.warn('block.left_px is null', block);
                }
                div.style.position = 'absolute';
                div.style.opacity = '0.0';

                div.setAttribute('data-slider', `${block.id}`);

                // https://jqueryui.com/draggable/#events
                (<any>jQuery(div)).draggable({
                    axis: 'x',
                    start: () => {
                        console.log('addDraggableSizeElementsForPeriod, Start');
                        this.showAddRemoveIcons(false);
                        this.enableShowingAddRemoveIcons = false;
                    },
                    drag: (e: Event, ui: any) => {
                        setTimeout(() => {
                            this.showAddRemoveIcons(false);
                            this.enableShowingAddRemoveIcons = false;
                            updateDraggableElement(
                                e,
                                ui,
                                'drag',
                                middle_px,
                                block.rounded.rounded_divider,
                                block.rounded.rounded_unit,
                                block.rounded.original_unit
                            );
                            // this.sliderData.calculateLabelPositions();
                            this.reloadInputsElements(true);
                            this.callbacks.onSliderDragMove(this.sliderData);
                        }, 0);
                    },
                    stop: (e: Event, ui: any) => {
                        console.log(
                            'addDraggableSizeElementsForPeriod, Stop',
                            middle_px,
                            block.rounded
                        );
                        this.enableShowingAddRemoveIcons = true;
                        setTimeout(() => {
                            updateDraggableElement(
                                e,
                                ui,
                                'stop',
                                middle_px,
                                block.rounded.rounded_divider,
                                block.rounded.rounded_unit,
                                block.rounded.original_unit
                            );
                            // this.sliderData.calculateLabelPositions();
                            this.reloadDraggableElements();
                            this.reloadSVGBackground();
                            this.reloadInputsElements(true);
                            this.callbacks.onSliderChange(this.sliderData);
                        }, 0);
                    },
                });
                if (draggable_container !== null) {
                    draggable_container.appendChild(div);
                } else {
                    throw new Error('draggable_container is null');
                }
            });
        }
    }

    private addDraggablePeriodBody(): void {
        const setVisibleElementDraggable = isVisible => {
            let elements = this.element.querySelectorAll(
                '.telematics-tg-ui-color-multi-range-grip'
            ) as NodeListOf<HTMLDivElement>;
            elements.forEach((element: HTMLDivElement) => {
                element.style.display = isVisible ? 'block' : 'none';
            });
        };

        const updateDraggablePeriod = (e: Event, ui: any, dragAction: 'drag' | 'stop') => {
            let this_draggable_element = e.target as HTMLDivElement;
            let new_left_px = parseInt(this_draggable_element.style.left.replace('px', ''));
            let element_width = parseInt(this_draggable_element.style.width.replace('px', ''));
            let min_range = 0;
            let max_range = this.sliderData.getSliderWidth();
            if (new_left_px <= min_range) {
                new_left_px = min_range;
                ui.position.left = new_left_px;
                this_draggable_element.style.left = `${new_left_px}px`;
                this_draggable_element.style.width = `${element_width}px`;
                this.sliderData.setInfinityOnBlock(0, 'from');
            } else {
                this.sliderData.setInfinityOnBlock(0, '');
            }
            if (new_left_px + element_width >= max_range) {
                new_left_px = max_range - element_width;
                ui.position.left = new_left_px;
                this_draggable_element.style.left = `${new_left_px}px`;
                this_draggable_element.style.width = `${element_width}px`;
                this.sliderData.setInfinityOnBlock(1, 'to');
            } else {
                this.sliderData.setInfinityOnBlock(1, '');
            }

            if (this.options.periodMode) {
                this.sliderData.setInfinityOnBlock(0, '');
                this.sliderData.setInfinityOnBlock(1, '');
            }
            // this.sliderData.setInfinityOnBlock(0,'');
            // this.sliderData.setInfinityOnBlock(1,'');
            this.sliderData.updateBlockLeftPx(0, new_left_px, true);
            this.sliderData.updateBlockLeftPx(1, new_left_px + element_width, true);
            this.reloadSVGBackground();
            this.reloadInputsElements();
        };

        let draggable_container = this.element.querySelector(
            '.js-draggable-container'
        ) as HTMLDivElement;
        if (draggable_container) {
            // draggable_container.innerHTML = '';
            if (this.options.sliderMode === 'range' && this.sliderData.getBlocks().length == 2) {
                let block = this.sliderData.getBlocks()[1];
                // console.info('Add draggable element (to move period), OK', block);
                let div = document.createElement('div');
                div.style.left = `${block.left_px - block.width_px}px`;
                div.style.width = `${block.width_px}px`;
                div.style.height = this.sliderHeightPx + 'px';
                div.style.position = 'absolute';
                // div.style.border = '1px solid #000';
                div.style.cursor = 'ew-resize';

                // div.addEventListener('dragstart', dragStart);
                // div.addEventListener('dragend', dragEnd);
                // div.addEventListener('drag', drag);

                draggable_container.appendChild(div);

                // https://jqueryui.com/draggable/#events
                (<any>jQuery(div)).draggable({
                    axis: 'x',
                    start: () => {
                        console.log('Start');
                        this.showAddRemoveIcons(false);
                        this.enableShowingAddRemoveIcons = false;
                        setVisibleElementDraggable(false);
                    },
                    drag: (e: Event, ui: any) => {
                        setTimeout(() => {
                            this.showAddRemoveIcons(false);
                            this.enableShowingAddRemoveIcons = false;
                            updateDraggablePeriod(e, ui, 'drag');
                            // this.sliderData.calculateLabelPositions();
                            this.reloadInputsElements(true);
                            this.callbacks.onSliderDragMove(this.sliderData);
                        }, 0);
                    },
                    stop: (e: Event, ui: any) => {
                        console.log('Stop');
                        this.enableShowingAddRemoveIcons = true;
                        // setTimeout is needed here not for smooth dragging, but to update slider after dragging
                        setTimeout(() => {
                            updateDraggablePeriod(e, ui, 'stop');
                            setVisibleElementDraggable(true);
                            // this.sliderData.calculateLabelPositions();
                            this.reloadDraggableElements();
                            this.reloadSVGBackground();
                            this.reloadInputsElements(true);
                            this.callbacks.onSliderChange(this.sliderData);
                        }, 0);
                    },
                });
            } else {
                console.info(
                    'Add draggable element (to move period), not passed ',
                    this.sliderData.getVisibleBlocksWithoutLast()
                );
            }
        }
    }

    private reloadDraggableElements(): void {
        let draggable_container = this.element.querySelector(
            '.js-draggable-container'
        ) as HTMLDivElement;
        if (draggable_container) {
            draggable_container.innerHTML = '';
        }

        if (this.options?.periodMode === true) {
            this.addDraggablePeriodBody();
            this.addDraggablePeriodMargins();
        } else {
            switch (this.options.sliderMode) {
                case 'range':
                    this.addDraggablePeriodBody();
                    break;
                case 'colorize':
                    this.addBlocksAllowingChangeColor(draggable_container);
                    break;
                default:
                    console.error('Unknown slider mode', this.options.sliderMode);
            }
            this.addDraggableValuesIndicators();
        }
    }

    private reloadInputsElements(calculateLabelPositions: boolean = false): void {
        let inputs_container = this.element.querySelector('.js-inputs-container') as HTMLDivElement;
        inputs_container.innerHTML = '';
        if (this.options.inputsAboveSlider) {
            inputs_container.style.top = `-${this.sliderHeightPx * 2 + 8}px`;
        }

        if (calculateLabelPositions && this.options.sliderMode === 'colorize') {
            this.sliderData.calculateLabelPositions(this.options.sliderMode === 'colorize');
        }

        // show values above slider (but not inputs)
        let values_container_above = this.element.querySelector(
            '.js-values-container-above'
        ) as HTMLDivElement;
        if (values_container_above) {
            if (this.options.sliderMode === 'range') {
                this.sliderData.calculateLabelPositions(false);
            }
            if (this.options.periodMode === true) {
                values_container_above.innerHTML = '';
                this.sliderData.getVisibleBlocks().forEach(block => {
                    let div = document.createElement('div');
                    div.classList.add('tg-ui-color-multi-range-value-above-slider');
                    div.style.position = 'absolute';
                    div.style.left = `${block.left_input_px}px`;
                    div.style.transform = `translateX(-50%)`;
                    if (
                        this.options.resolution === 'number' ||
                        this.options.resolution === 'today'
                    ) {
                        div.innerHTML = `<div class="rounded_title" style="width: 40px; text-align: ${block.id ? 'right' : 'left'};">${block.rounded.rounded_value} ${block.rounded.rounded_title}</div>`;
                    } else if (this.options.resolution === 'date') {
                        div.innerHTML = `<div class="rounded_title" style="width: 90px; text-align: ${block.id ? 'right' : 'left'};">${this.resolutionConverter.getFormatted(block.max, true)}</div>`;
                    } else {
                        div.innerHTML = `<div class="rounded_title" style="width: 40px; text-align:  ${block.id ? 'right' : 'left'};">${this.resolutionConverter.getFormatted(block.max)}</div>`;
                    }
                    values_container_above.appendChild(div);
                });
            }
        } else {
            throw new Error('values_container_above not found');
        }

        // show inputs below slider
        if (this.options.periodMode === true) {
            this.showPeriodInputsBelowSlider(inputs_container);
        } else {
            this.showStandardInputsBelowSlider(inputs_container);
        }
    }

    private showPeriodInputsBelowSlider(inputs_container: HTMLDivElement): void {
        // https://er.automapa.pl/issues/34839

        let periodValues = this.sliderData.getPeriodValuesForTwoBlocks(0, 1);
        // let input_value: string = String(periodValues.middle.rounded_value);

        let div = document.createElement('div');
        div.classList.add('telematics-tg-ui-color-multi-range-value');
        div.style.position = 'absolute';
        // div.style.left = `${this.sliderData.convertValueToPx(periodValues.middle.non_rounded_value)}px`;
        div.style.left = `${this.sliderData.getVisibleMediumRangePx()}px`;
        // if (this.options.resolution === 'datetime' || this.options.resolution === 'date') {
        //     div.style.left = `${this.sliderData.getVisibleMediumRangePx()-21}px`;
        // }
        div.style.top = `-2px`;
        div.style.transform = `translateX(-23px)`;
        if (this.options.resolution !== 'number' && this.options.resolution !== 'today') {
            let width = this.options.resolution === 'time' ? '41px' : '41px';
            div.innerHTML = `
                <div class="telematics-color-period">
                    <div data-desc="middle">
                            <input
                                class="js-input-middle"
                                type="text"
                                value="${this.resolutionConverter.getFormatted(periodValues.middle.non_rounded_value, true)}"
                                data-value="${periodValues.middle.non_rounded_value}"
                                data-divider="${periodValues.middle.rounded_divider}"
                                data-last-correct-value="${periodValues.middle.rounded_value}"
                                data-type="smart-rounded-enabled"
                                style="width: ${width};" 
                            />
                            <div style="position: absolute; top: -9999px; left: -9999px; width: auto; font-size: 11px; font-weight: 400; letter-spacing: 0; white-space: nowrap;">
                                ${String(periodValues.middle.rounded_value)}
                            </div>
                            <div class="rounded_title"></div>
                    </div>
                    <div style="font-size: 15px; padding: 2px 4px;">&#177;</div>
                    <div>
                            <input
                                class="js-input-padding"                            
                                type="text"
                                value="${String(periodValues.padding.rounded_value)}"
                                data-value="${periodValues.padding.non_rounded_value}"
                                data-divider="${periodValues.padding.rounded_divider}"
                                data-last-correct-value="${periodValues.padding.rounded_value}"
                                data-type="smart-rounded-enabled"
                                style="width: 41px;" 
                            />
                            <div style="position: absolute; top: -9999px; left: -9999px; width: auto; font-size: 11px; font-weight: 400; letter-spacing: 0; white-space: nowrap;">
                                ${String(periodValues.padding.rounded_value)}
                            </div>
                            <div class="rounded_title">${periodValues.padding.rounded_title}</div>
                    </div>                            
                </div>                            
                `;
            if (this.options.resolution === 'date') {
                //disable input for date
                div.querySelector('.js-input-middle').setAttribute('disabled', 'disabled');
            } else {
                div.querySelector('.js-input-middle').removeAttribute('disabled');
            }

            // let el = div.querySelector('.calendar-today') as HTMLInputElement;
            // if (el) {
            //     // console.log('calendar-today, click');
            //     this.resolutionConverter.addCalendarPlugin(
            //         el,
            //         this.sliderData,
            //         0,
            //         (day_string, value) => {
            //             (div.querySelector('.js-input-middle') as HTMLInputElement).value = ResolutionDate.getShortFormat(new Date(day_string));
            //             updateSliderData(
            //                 div.querySelector('.js-input-middle') as HTMLInputElement,
            //                 div.querySelector('.js-input-padding') as HTMLInputElement
            //             );
            //         }
            //     );
            // }
        } else {
            div.innerHTML = `
                <div class="telematics-color-period">
                    <div data-desc="middle">
                            <input
                                class="js-input-middle"
                                type="text"
                                value="${String(periodValues.middle.rounded_value)}"
                                data-value="${periodValues.middle.non_rounded_value}"
                                data-divider="${periodValues.middle.rounded_divider}"
                                data-last-correct-value="${periodValues.middle.rounded_value}"
                                data-type="smart-rounded-enabled"
                                style="width: 41px;" 
                            />
                            <div style="position: absolute; top: -9999px; left: -9999px; width: auto; font-size: 11px; font-weight: 400; letter-spacing: 0; white-space: nowrap;">
                                ${String(periodValues.middle.rounded_value)}
                            </div>
                            <div class="rounded_title">${periodValues.middle.rounded_title}</div>
                    </div>
                    <div style="font-size: 15px; padding: 2px 4px;">&#177;</div>
                    <div>
                            <input
                                class="js-input-padding"                            
                                type="text"
                                value="${String(periodValues.padding.rounded_value)}"
                                data-value="${periodValues.padding.non_rounded_value}"
                                data-divider="${periodValues.padding.rounded_divider}"
                                data-last-correct-value="${periodValues.padding.rounded_value}"
                                data-type="smart-rounded-enabled"
                                style="width: 41px;" 
                            />
                            <div style="position: absolute; top: -9999px; left: -9999px; width: auto; font-size: 11px; font-weight: 400; letter-spacing: 0; white-space: nowrap;">
                                ${String(periodValues.padding.rounded_value)}
                            </div>
                            <div class="rounded_title">${periodValues.padding.rounded_title}</div>
                    </div>                            
                </div>                            
                `;
        }

        const updateSliderData = (
            inputMiddle: HTMLInputElement,
            inputPadding: HTMLInputElement,
            inputMiddleValue: number
        ) => {
            try {
                let value_middle: number;
                let new_middle: number;
                if (this.options.resolution !== 'number' && this.options.resolution !== 'today') {
                    let valueStr = this.resolutionConverter.getFormatted(inputMiddleValue, false);
                    if (this.resolutionConverter.isValid(valueStr) === false)
                        throw new Error('value_middle is not valid: ' + valueStr);
                    value_middle = this.resolutionConverter.getNumber(valueStr);
                } else {
                    value_middle = Number(inputMiddle.value.replace(',', '.'));
                }
                if (isNaN(value_middle)) throw new Error('value_middle is NaN');
                console.debug(
                    'updateSliderData, currentUnit',
                    periodValues.middle.rounded_unit,
                    ', target_unit',
                    this.sliderData.getUnit()
                );
                if (this.options.resolution !== 'number' && this.options.resolution !== 'today') {
                    new_middle = value_middle;
                } else {
                    new_middle = new smartRounding().getOriginalValueFromRounded(
                        value_middle,
                        periodValues.middle.rounded_divider,
                        periodValues.middle.rounded_unit,
                        this.sliderData.getUnit()
                    );
                    if (isNaN(new_middle)) throw new Error('new_middle is NaN');
                }

                let value_padding: number;
                value_padding = Number(inputPadding.value.replace(',', '.'));
                if (isNaN(value_padding)) throw new Error('value_padding is NaN');
                let new_padding: number;
                if (this.options.resolution !== 'number' && this.options.resolution !== 'today') {
                    new_padding = value_padding;
                } else {
                    new_padding = new smartRounding().getOriginalValueFromRounded(
                        value_padding,
                        periodValues.padding.rounded_divider,
                        periodValues.padding.rounded_unit,
                        this.sliderData.getUnit()
                    );
                }
                this.sliderData.setMiddleValuesForTwoBlocks(0, 1, new_middle, new_padding);
            } catch (e) {
                console.warn('error: ' + e.message);
            }
            this.reload();
            this.callbacks.onSliderChange(this.sliderData);
        };

        div.querySelector('.js-input-middle').addEventListener('change', event => {
            updateSliderData(
                div.querySelector('.js-input-middle') as HTMLInputElement,
                div.querySelector('.js-input-padding') as HTMLInputElement,
                periodValues.middle.non_rounded_value
            );
        });

        div.querySelector('.js-input-padding').addEventListener('change', event => {
            console.log('js-input-padding, change', periodValues.middle.non_rounded_value);
            updateSliderData(
                div.querySelector('.js-input-middle') as HTMLInputElement,
                div.querySelector('.js-input-padding') as HTMLInputElement,
                periodValues.middle.non_rounded_value
            );
        });

        inputs_container.appendChild(div);
    }

    private showStandardInputsBelowSlider(inputs_container: HTMLDivElement): void {
        const refreshInputRoundedTitleVisibility = (div: HTMLDivElement) => {
            // let input = e.target as HTMLInputElement;
            let input = div.querySelector('input') as HTMLInputElement;
            // console.log('refreshInputRoundedTitleVisibility, keyup', input.value);
            if (input.value.toUpperCase() === 'MAX' || input.value.toUpperCase() === 'MIN') {
                // console.log('refreshInputRoundedTitleVisibility, keyup, hidden');
                (div.querySelector('.rounded_title') as HTMLDivElement).style.display = 'none';
            } else {
                // console.log('refreshInputRoundedTitleVisibility, keyup, visible');
                (div.querySelector('.rounded_title') as HTMLDivElement).style.display = 'block';
            }
        };

        let block_number = -1;
        // this.sliderData.getVisibleBlocksWithoutLast(this.options.sliderMode === 'colorize').forEach((block) => {
        this.sliderData
            .getVisibleBlocksWithoutLast(this.options.sliderMode === 'colorize')
            .forEach(block => {
                // console.debug('showStandardInputsBelowSlider, block', block);
                // if (block.max <= this.sliderData.getVisibleMax()) {
                if (block.left_px <= this.sliderData.getSliderWidth()) {
                    block_number++;
                    // <div class="tg-ui-color-multi-range-value" style="left: 139.817px; z-index: 100;" data-slider="3" data-pxi="3">  <input type="text" value="14.9" data-value="3" data-divider="1000" data-last-correct-value="14.9" style="width: 41px;"><div style="position: absolute; top: -9999px; left: -9999px; width: auto; font-size: 11px; font-family: MS Shell Dlg \32 ; font-weight: 400; letter-spacing: 0px; white-space: nowrap;">14.9</div>   <div class="rounded_title">tys.</div>   </div>
                    let div = document.createElement('div');
                    let value = this.sliderData.getSmoothRoundedValue(
                        block.max,
                        this.sliderData.getUnit()
                    );
                    // console.debug('showStandardInputsBelowSlider, value', value);
                    div.className = 'telematics-tg-ui-color-multi-range-value';
                    if (this.options.sliderMode === 'range') {
                        if (block_number === 0 && this.sliderData.getBlocks().length > 1) {
                            div.className += ' telematics-color-multi-range-left';
                        } else {
                            div.className += ' telematics-color-multi-range-right';
                        }
                    }
                    if (this.options.smallInputs) {
                        div.className += ' small-inputs';
                    }
                    if (this.options.inputsAboveSlider) {
                        div.className += ' inputs-above-slider';
                    }
                    if (this.options.sliderMode === 'range') {
                        div.style.left = `${block?.left_px}px`;
                    } else {
                        // keep magnetic distance
                        div.style.left = `${block?.left_input_px}px`;
                    }
                    div.style.zIndex = '100';
                    div.setAttribute('data-slider', `${block.id}`);
                    div.setAttribute('data-pxi', `${block.id}`);
                    // console.log('getVisibleBlocks, block', block);
                    if (this.options?.enableSmartRounding === true) {
                        if (block.rounded !== null) {
                            // let input_value: string = String(block.rounded.rounded_value);
                            let input_value: string = String(value.rounded_value);
                            if (
                                this.options.resolution !== 'number' &&
                                this.options.resolution !== 'today'
                            ) {
                                input_value = this.resolutionConverter.getFormatted(
                                    block.max,
                                    true
                                );
                            }
                            if (block.infinity_type === 'from') {
                                input_value = 'MIN';
                            }
                            if (block.infinity_type === 'to') {
                                input_value = 'MAX';
                            }
                            if (
                                this.options.resolution !== 'number' &&
                                this.options.resolution !== 'today'
                            ) {
                                let width = this.options.resolution === 'time' ? '41px' : '42px';
                                div.innerHTML = `
                                <div style="display: flex; align-items: center;"> 
                                    <input
                                            type="text"
                                            value="${input_value}"
                                            data-value="${value.rounded_value}"
                                            data-divider="${value.rounded_divider}"
                                            data-last-correct-value="${value.rounded_value}"
                                            data-type="smart-rounded-enabled"
                                            style="width: ${width};">
                                    <div>
                                        <div class="calendar-today" style="display:${this.options.resolution === 'time' ? 'none' : 'flex'}"></div>                                    
                                    </div>
                                </div>
                                <div style="position: absolute; top: -9999px; left: -9999px; width: auto; font-size: 11px; font-weight: 400; letter-spacing: 0; white-space: nowrap;">
                                    ${input_value}
                                </div>
                                <div class="rounded_title" data-type="title"></div>                            
                            `;
                                if (this.options.resolution === 'date') {
                                    div.querySelector('input').setAttribute('disabled', 'disabled');
                                } else {
                                    div.querySelector('input').removeAttribute('disabled');
                                }
                            } else {
                                div.innerHTML = `
                            <input
                                    type="text"
                                    value="${input_value}"
                                    data-value="${value.rounded_value}"
                                    data-divider="${value.rounded_divider}"
                                    data-last-correct-value="${value.rounded_value}"
                                    data-type="smart-rounded-enabled"
                                    style="width: 41px;">
                                <div style="position: absolute; top: -9999px; left: -9999px; width: auto; font-size: 11px; font-weight: 400; letter-spacing: 0; white-space: nowrap;">
                                    ${input_value}
                                </div>
                                <div class="rounded_title" data-type="title">${value.rounded_title}</div>
                                `;
                            }
                        } else {
                            console.warn('block.rounded is null', block);
                        }
                    } else {
                        let input_value: string = String(Math.round(block.max * 10) / 10);
                        if (block.infinity_type === 'from') {
                            input_value = 'MIN';
                        }
                        if (block.infinity_type === 'to') {
                            input_value = 'MAX';
                        }

                        if (
                            this.options.resolution !== 'number' &&
                            this.options.resolution !== 'today'
                        ) {
                            div.innerHTML = `<div>${input_value}</div>`;
                        } else {
                            div.innerHTML = `
                        <input
                                type="text"
                                value="${input_value}"
                                data-value="${block.max}"
                                data-divider="1"
                                data-last-correct-value="${Math.round(block?.max * 10) / 10}"
                                data-type="smart-rounded-disabled"
                                style="width: 41px;"
                                >
                            <div style="position: absolute; top: -9999px; left: -9999px; width: auto; font-size: 11px; font-weight: 400; letter-spacing: 0; white-space: nowrap;">
                                ${input_value}
                            </div>
                            <div class="rounded_title" data-type="unit">${this.sliderData.getUnit()}</div>
                        `;
                        }
                    }

                    refreshInputRoundedTitleVisibility(div);

                    let el = div.querySelector('.calendar-today') as HTMLInputElement;
                    if (el) {
                        // console.log('calendar-today, click');
                        this.resolutionConverter.addCalendarPlugin(
                            el,
                            this.sliderData,
                            block.id,
                            (day_string, value) => {
                                // console.log('calendar-today, click, day', day_string, value);
                                if (value > this.sliderData.getVisibleMax()) {
                                    this.sliderData.setVisibleMinMax(
                                        this.sliderData.getVisibleMin(),
                                        value,
                                        false,
                                        false
                                    );
                                }
                                if (value < this.sliderData.getVisibleMin()) {
                                    this.sliderData.setVisibleMinMax(
                                        value,
                                        this.sliderData.getVisibleMax(),
                                        false,
                                        false
                                    );
                                }
                                this.sliderData.setInfinityOnBlock(block.id, '');
                                this.sliderData.updateBlock(block.id, value, block.color);
                                this.callbacks.onSliderChange(this.sliderData);
                                this.reload();
                            }
                        );
                    }

                    (div.querySelector('input') as HTMLInputElement).addEventListener(
                        'input',
                        e => {
                            refreshInputRoundedTitleVisibility(div);
                        }
                    );

                    (div.querySelector('input') as HTMLInputElement).addEventListener(
                        'change',
                        e => {
                            // On change input value set new value to block
                            let input = e.target as HTMLInputElement;
                            let newValue: number = 0;

                            console.log('slider, change', e, input);

                            if (input.value.toUpperCase() === 'MIN') {
                                this.sliderData.setInfinityOnBlock(block.id, 'from');
                                this.sliderData.updateBlockMax(
                                    block.id,
                                    this.sliderData.getVisibleMin()
                                );
                                this.callbacks.onSliderChange(this.sliderData);
                                // (div.querySelector('.rounded_title') as HTMLDivElement).style.display = 'none';
                                this.reload();
                                return true;
                            } else if (input.value.toUpperCase() === 'MAX') {
                                this.sliderData.setInfinityOnBlock(block.id, 'to');
                                this.sliderData.updateBlockMax(
                                    block.id,
                                    this.sliderData.getVisibleMax()
                                );
                                this.callbacks.onSliderChange(this.sliderData);
                                // (div.querySelector('.rounded_title') as HTMLDivElement).style.display = 'none';
                                this.reload();
                                return true;
                            } else {
                                this.sliderData.setInfinityOnBlock(block.id, '');
                                // (div.querySelector('.rounded_title') as HTMLDivElement).style.display = 'block';
                            }

                            if (
                                this.options.resolution !== 'number' &&
                                this.options.resolution !== 'today'
                            ) {
                                if (this.resolutionConverter.isValid(input.value) === false) {
                                    console.error('Error while parsing input.value', input.value);
                                    return false;
                                }
                                newValue = this.resolutionConverter.getNumber(input.value);
                            } else {
                                try {
                                    newValue = new smartRounding().getOriginalValueFromRounded(
                                        parseFloat(input.value.replace(',', '.')),
                                        value.rounded_divider,
                                        value.rounded_unit,
                                        this.sliderData.getUnit()
                                    );
                                    console.log(
                                        'smartRounding',
                                        parseFloat(input.value.replace(',', '.')),
                                        value.rounded_divider,
                                        value.rounded_unit,
                                        this.sliderData.getUnit(),
                                        ' => ',
                                        newValue
                                    );
                                } catch (e) {
                                    console.error('Error while parsing input.value', e);
                                    // TODO: show error message or change class to error
                                    return false;
                                }
                            }
                            if (isNaN(newValue)) {
                                console.error('Error while parsing input.value', e);
                                return false;
                            }
                            // let divider = parseFloat(input.getAttribute('data-divider') as string);
                            if (block !== null) {
                                block.max = newValue; //  * block.rounded.rounded_divider
                                block.infinity_type = '';
                                if (block.max > this.sliderData.getVisibleMax()) {
                                    block.max = this.sliderData.getVisibleMax();
                                }
                                if (block.max < this.sliderData.getVisibleMin()) {
                                    block.max = this.sliderData.getVisibleMin();
                                }
                                this.sliderData.updateBlock(block.id, block.max, block.color);
                                this.callbacks.onSliderChange(this.sliderData);
                                this.reload();
                            }
                            // console.log('set block to new value', block);
                        }
                    );

                    inputs_container.appendChild(div);
                }
            });
    }

    public reloadSVGBackground(): void {
        let svg_container = this.element.querySelector('.js-svg-container') as HTMLDivElement;
        if (!svg_container) {
            alert('svg_container is null');
        }
        // (this.element.querySelector('.telematics-tg-ui-color-multi-range') as HTMLTableElement).style.display = 'none';

        switch (this.options.sliderMode) {
            case 'range':
                svg_container.innerHTML = this.getSVGForRangeMode();
                break;
            case 'colorize':
                svg_container.innerHTML = this.getSVGForColorizeMode();
                break;
            default:
                alert('Unknown slider mode: ' + this.options.sliderMode);
        }
    }

    private addBlocksAllowingChangeColor(draggable_container: HTMLDivElement): void {
        let blocks = this.sliderData.getBlocks();
        const MAX_COLORIZE_BLOCKS = 6;

        for (let i = 0; i < blocks.length; i++) {
            let block = blocks[i];
            let div = document.createElement('div') as HTMLDivElement;
            div.style.left = `${block.left_px - block.width_px}px`;
            div.style.width = `${block.width_px}px`;
            div.style.height = this.sliderHeightPx + 'px';
            div.style.position = 'absolute';
            div.style.opacity = '0.0';
            div.style.cursor = 'pointer';

            // add event for color picker
            div.addEventListener('click', (e: MouseEvent) => {
                let block_id = block.id;
                // console.log('clicked in block_id', block_id);
                if (this.enableColorPicker) {
                    this.colorPicker.setPosition(
                        this.element.getBoundingClientRect().left + 10,
                        this.element.getBoundingClientRect().top + 34
                    );
                    this.colorPicker.setColor(this.sliderData.getBlock(block_id).color);
                    this.colorPicker.show(block_id);
                }
            });

            // add event for add and remove icons
            div.addEventListener('mouseenter', (e: MouseEvent) => {
                // let block_id = parseInt((<HTMLElement>e.target).getAttribute('data-slider') ?? '');
                let block_id = block.id;
                console.log('enter on block', block_id, this.sliderData.getBlockLeftPx(block_id));
                let add_icon = this.element.querySelector(
                    '.tg-ui-color-multi-range-add'
                ) as HTMLDivElement;
                let remove_icon = this.element.querySelector(
                    '.tg-ui-color-multi-range-remove'
                ) as HTMLDivElement;
                let new_max = this.sliderData.getMiddleValueOfThisBlock(block_id);
                let add_icon_position = this.sliderData.convertValueToPx(new_max) - 5;
                let remove_icon_position =
                    this.sliderData.getBlockLeftPx(block_id) - this.sliderGripWidthPx / 2;
                add_icon_position = Math.min(remove_icon_position - 11, add_icon_position);

                if (add_icon) {
                    if (this.sliderData.getVisibleBlocks().length < MAX_COLORIZE_BLOCKS) {
                        add_icon.style.left = add_icon_position + 'px';
                        if (this.enableShowingAddRemoveIcons) add_icon.style.display = 'block';
                        add_icon.setAttribute('data-slider', `${block_id}`);
                    } else {
                        add_icon.style.display = 'none';
                    }
                }

                if (remove_icon) {
                    if (this.sliderData.getVisibleBlocks().length > 1 && i < blocks.length - 1) {
                        remove_icon.style.left = remove_icon_position + 'px';
                        if (this.enableShowingAddRemoveIcons) remove_icon.style.display = 'block';
                        remove_icon.setAttribute('data-slider', `${block_id}`);
                    } else {
                        remove_icon.style.display = 'none';
                    }
                }
            });

            draggable_container.appendChild(div);
        }
    }

    private getSVGForRangeMode(): string {
        let svg: string = '';
        const totalHeight = this.sliderData.getSliderHeight();
        const totalWidth = this.sliderData.getSliderWidth();
        const blockNegativeColor = `#cb0936`;
        // const blockNegativeColor = `#a4d86a`; // green
        const backgroundNegativeColor = `#d5bcba`;
        const backgroundPositiveColor = `#bcbcba`;
        const strokeWidthForDangerZoneBorder = 1;
        const dangerZoneBorderColor = `#888888`;
        const indicatorColor = this.options.indicatorColor;

        const dangerZoneBelowPx: number | false =
            typeof this.options.dangerZoneBelowValue === 'number'
                ? Math.min(
                      this.sliderData.convertValueToPx(this.options.dangerZoneBelowValue),
                      totalWidth
                  )
                : false;
        const dangerZoneAbovePx: number | false =
            typeof this.options.dangerZoneAboveValue === 'number'
                ? Math.max(this.sliderData.convertValueToPx(this.options.dangerZoneAboveValue), 0)
                : false;

        // Background total (positive)
        svg += svgDrawer.Rect(
            0,
            totalWidth,
            totalHeight,
            backgroundPositiveColor,
            'background positive'
        );

        // Background negative below  (if exists)
        if (
            dangerZoneBelowPx !== false &&
            dangerZoneBelowPx > 0 &&
            dangerZoneBelowPx <= totalWidth
        ) {
            svg += svgDrawer.Rect(
                0,
                dangerZoneBelowPx,
                totalHeight,
                backgroundNegativeColor,
                'background negative'
            );
            svg += svgDrawer.Line(
                dangerZoneBelowPx,
                1,
                totalHeight,
                dangerZoneBorderColor,
                'zero line'
            );
        }

        // Background negative above  (if exists)
        if (
            dangerZoneAbovePx !== false &&
            dangerZoneAbovePx >= 0 &&
            dangerZoneAbovePx <= totalWidth
        ) {
            svg += svgDrawer.Rect(
                dangerZoneAbovePx,
                totalWidth - dangerZoneAbovePx,
                totalHeight,
                backgroundNegativeColor,
                'background negative'
            );
            svg += svgDrawer.Line(
                dangerZoneAbovePx,
                1,
                totalHeight,
                dangerZoneBorderColor,
                'zero line'
            );
        }

        // Blocks
        // let blockCount = this.sliderData.getBlocks().length;
        // this.sliderData.getVisibleBlocks().slice(blockCount-1,blockCount).forEach((block) => {
        if (this.sliderData.getBlocks().length >= 1 && this.sliderData.getBlocks().length <= 2) {
            let [min_px, max_px] = this.sliderData.getVisibleRangePx();
            let block = this.sliderData.getBlocks()[this.sliderData.getBlocks().length - 1];
            let width_px = Math.abs(max_px - min_px);
            let x = min_px;
            svg += svgDrawer.Rect(x, width_px, totalHeight, block.color, 'positive block');

            if (dangerZoneBelowPx !== false && dangerZoneBelowPx > x) {
                //  && dangerZoneBelowPx < x + width_px
                let width: number = dangerZoneBelowPx - x;
                if (width > width_px) width = width_px;
                svg += svgDrawer.Rect(
                    x,
                    width,
                    totalHeight,
                    blockNegativeColor,
                    'background below negative block'
                );
                svg += svgDrawer.Line(
                    dangerZoneBelowPx,
                    strokeWidthForDangerZoneBorder,
                    totalHeight,
                    dangerZoneBorderColor,
                    'end line in below negative block'
                );
            }

            if (dangerZoneAbovePx !== false && dangerZoneAbovePx - width_px < x) {
                // let width: number = x - dangerZoneAbovePx + width_px;
                let width = Math.min(x - dangerZoneAbovePx + width_px, width_px);
                svg += svgDrawer.Rect(
                    Math.max(x, dangerZoneAbovePx),
                    width,
                    totalHeight,
                    blockNegativeColor,
                    'background above negative in block'
                );
                svg += svgDrawer.Line(
                    dangerZoneAbovePx,
                    strokeWidthForDangerZoneBorder,
                    totalHeight,
                    dangerZoneBorderColor,
                    'begin line in above negative block'
                );
            }

            if (
                this.options?.periodMode === true &&
                this.options?.sliderMode === 'range' &&
                this.sliderData.getBlocks().length === 2
            ) {
                let periodValues = this.sliderData.getPeriodValuesForTwoBlocks(0, 1);
                let middlePx = this.sliderData.convertValueToPx(periodValues.middle.original_value);
                // console.debug('slider_middle, middlePx', middlePx, periodValues);
                svg += svgDrawer.Line(
                    middlePx,
                    this.strokeWidthForValueIndicator,
                    totalHeight,
                    indicatorColor,
                    'value indicator for period middle'
                );
            }
        }

        if (this.options?.sliderMode === 'range' && this.sliderData.getBlocks().length === 2) {
            if (
                this.minimumWidthForTrianglesPx <=
                Math.abs(
                    this.sliderData.getBlocks()[1].left_px - this.sliderData.getBlocks()[0].left_px
                )
            ) {
                svg += svgDrawer.Triangle(
                    this.sliderData.getBlocks()[0].left_px,
                    'right',
                    'left triangle'
                );
                svg += svgDrawer.Triangle(
                    this.sliderData.getBlocks()[1].left_px,
                    'left',
                    'right triangle'
                );
            } else {
                svg += svgDrawer.Line(
                    this.sliderData.getBlocks()[0].left_px,
                    1,
                    totalHeight,
                    indicatorColor,
                    'just line indicator for first value'
                );
                svg += svgDrawer.Line(
                    this.sliderData.getBlocks()[1].left_px,
                    1,
                    totalHeight,
                    indicatorColor,
                    'just line indicator for second value'
                );
            }
        } else {
            this.sliderData.getBlocks().forEach(block => {
                svg += svgDrawer.Line(
                    block.left_px,
                    this.strokeWidthForValueIndicator,
                    totalHeight,
                    indicatorColor,
                    'just line indicator for value'
                );
            });
        }

        // console.log('svg', svg);
        return `<svg viewBox="0 0 ${totalWidth} ${totalHeight}" xmlns="http://www.w3.org/2000/svg">${svg}</svg>`;
    }

    private getSVGForColorizeMode(): string {
        let svg: string = '';
        const totalHeight = this.sliderData.getSliderHeight();
        const totalWidth = this.sliderData.getSliderWidth();
        const backgroundPositiveColor = `#bcbcba`;
        const strokeWidthForValueIndicator = 3;
        const indicatorColor = this.options.indicatorColor;

        // Background total (positive)
        svg += svgDrawer.Rect(
            0,
            totalWidth,
            totalHeight,
            backgroundPositiveColor,
            'background positive'
        );

        // Blocks
        let blockCount = this.sliderData.getVisibleBlocks().length;
        this.sliderData.getBlocks().forEach(block => {
            let width_px = block.width_px;
            let x = block.left_px - block.width_px;
            svg += svgDrawer.Rect(x, width_px, totalHeight, block.color, 'positive block');
        });

        this.sliderData
            .getBlocks()
            .slice(0, blockCount)
            .forEach(block => {
                if (block.left_px > totalWidth) {
                    console.info(
                        'ColorizeSlider, dont show line indicator for value, because block.left_px > totalWidth',
                        block
                    );
                    return false;
                }
                if (block.left_px < 0) {
                    console.info(
                        'ColorizeSlider, dont show line indicator for value, because block.left_px < 0',
                        block
                    );
                    return false;
                }
                svg += svgDrawer.Line(
                    block.left_px,
                    strokeWidthForValueIndicator,
                    totalHeight,
                    indicatorColor,
                    'just line indicator for value'
                );
            });

        // console.log('svg', svg);
        return `<svg viewBox="0 0 ${totalWidth} ${totalHeight}"  shape-rendering="crispEdges" xmlns="http://www.w3.org/2000/svg">${svg}</svg>`;
    }

    getRelativeTopForInputsContainer() {
        if (this.options.sliderMode === 'range') {
            return 12;
        } else {
            // return -this.sliderData.getSliderHeight();
            return 3;
        }
    }

    private getHTML(): string {
        return (
            `
        <div style="height:${this.sliderHeightPx}px; position: relative;">
            <div class="js-svg-container" style="height:${this.sliderHeightPx}px;"></div>
            <div class="js-values-container-above" style="
                position: relative;
                height: 0;
                width: ${this.sliderData.getSliderWidth()}px;
                top: -33px;
                opacity: 0.7;
                "></div>
            <div class="js-inputs-container" style="
                position: relative;
                height: ${this.sliderHeightPx}px;
                width: ${this.sliderData.getSliderWidth()}px;
                top: ${this.getRelativeTopForInputsContainer()}px;
                z-index: 20;
            "></div>
            <div class="js-draggable-container" style="
            position: absolute;
            height: ${this.sliderHeightPx}px;
            width: ${this.sliderData.getSliderWidth()}px;
            top: 0;
            /*left: -1px;*/
            z-index: 21;
            ">
            </div>

            ` +
            (this.enableAddRemovePeriods
                ? `
            <div class="tg-ui-color-multi-range-add" style="top: -15px; left: 363px; display: none;"></div>
            <div class="tg-ui-color-multi-range-remove" style="top: -15px; left: 594px; display: none;"></div>
            `
                : '') +
            `
        </div>
        `
        );
    }
}

export default ColorizeSlider;
